// Burndown chart for a single cycle — hand-rolled SVG, no external chart library.
// Shows actual remaining tasks over time as a polyline, an ideal burn-down line
// from the first snapshot's total to 0, plus axis labels and current counts.
// When canEdit, a "Snapshot heute erstellen" button triggers an immediate snapshot.

import { useState, useEffect, useCallback } from "preact/hooks";
import type { ApiBurndown } from "@juuplan/shared";
import {
  getCycleBurndown,
  triggerCycleSnapshot,
  getCycleVelocity,
} from "../lib/api";

interface Props {
  cycleId: number;
  projectId: number;
  canEdit: boolean;
}

// SVG viewport constants
const W = 320;
const H = 160;
const PAD_LEFT = 36;
const PAD_RIGHT = 8;
const PAD_TOP = 8;
const PAD_BOTTOM = 24;
const PLOT_W = W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = H - PAD_TOP - PAD_BOTTOM;

/**
 * Hand-rolled SVG burndown chart with velocity readout.
 * Fetches getCycleBurndown on mount / cycleId change.
 */
export function CycleBurndown(props: Props): preact.JSX.Element {
  const { cycleId, projectId, canEdit } = props;

  const [burndown, setBurndown] = useState<ApiBurndown | null>(null);
  const [velocity, setVelocity] = useState<number | null | undefined>(
    undefined
  );
  // Getrennt von `velocity`, weil der Server `null` LEGITIM liefert ("noch
  // nicht genug abgeschlossene Zyklen") — ein Ladefehler darf nicht dieselbe
  // "—"-Anzeige wie dieser echte Zustand ergeben, sonst ist ein
  // Serverfehler von "keine Daten" nicht mehr zu unterscheiden.
  const [velocityLoadFailed, setVelocityLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snapshotting, setSnapshotting] = useState(false);

  const fetchBurndown = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCycleBurndown(cycleId);
      setBurndown(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  useEffect(() => {
    void fetchBurndown();
  }, [fetchBurndown]);

  // Velocity — fetch once per projectId. GET-Fehler werden von api() NICHT
  // getoastet (lib/api.ts: nur schreibende Methoden), und Velocity ist eine
  // optionale Zusatzangabe neben dem Burndown-Chart (das über fetchBurndown
  // seinen eigenen Fehlerzustand hat) — daher bewusst kein Toast, aber ein
  // eigener `velocityLoadFailed`-Zustand statt eines still gleich
  // aussehenden "—".
  useEffect(() => {
    getCycleVelocity(projectId)
      .then((r) => setVelocity(r.velocity))
      .catch(() => {
        setVelocityLoadFailed(true);
        setVelocity(null);
      });
  }, [projectId]);

  const handleSnapshot = async (): Promise<void> => {
    setSnapshotting(true);
    try {
      await triggerCycleSnapshot(cycleId);
      await fetchBurndown();
    } catch {
      // api wrapper already toasted
    } finally {
      setSnapshotting(false);
    }
  };

  // --- render ---

  if (loading) {
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] px-2 py-2">
        Lädt…
      </p>
    );
  }

  if (error) {
    return (
      <p class="text-xs text-[color:var(--color-error)] px-2 py-2">
        Fehler: {error}
      </p>
    );
  }

  if (!burndown) return <></>;

  const { current, snapshots } = burndown;
  const sorted = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));

  // Build SVG chart only when there are snapshots
  let chartEl: preact.JSX.Element | null = null;

  if (sorted.length >= 1) {
    const remainings = sorted.map((s) => s.total - s.completed);
    const maxRemaining = Math.max(1, ...remainings, sorted[0].total);
    const n = sorted.length;

    // Map snapshot index -> SVG x coord
    const xFor = (i: number): number =>
      n === 1 ? PAD_LEFT + PLOT_W / 2 : PAD_LEFT + (i / (n - 1)) * PLOT_W;

    // Map remaining value -> SVG y coord (0 = bottom of plot area)
    const yFor = (rem: number): number =>
      PAD_TOP + PLOT_H - (rem / maxRemaining) * PLOT_H;

    // Actual polyline
    const actualPoints = sorted
      .map((s, i) => `${xFor(i)},${yFor(s.total - s.completed)}`)
      .join(" ");

    // Ideal line: start = first snapshot total, end = 0 at last snapshot x
    const idealX0 = xFor(0);
    const idealX1 = n === 1 ? xFor(0) : xFor(n - 1);
    const idealY0 = yFor(sorted[0].total);
    const idealY1 = yFor(0);

    // Axis labels
    const firstDate = sorted[0].date.slice(5); // MM-DD
    const lastDate = sorted[n - 1].date.slice(5);

    chartEl = (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto" }}
        aria-label="Burndown-Diagramm"
      >
        {/* Y axis grid lines and labels */}
        {/* 0 line */}
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP + PLOT_H}
          x2={PAD_LEFT + PLOT_W}
          y2={PAD_TOP + PLOT_H}
          stroke="currentColor"
          stroke-width={0.5}
          opacity={0.3}
        />
        {/* Max line */}
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP}
          x2={PAD_LEFT + PLOT_W}
          y2={PAD_TOP}
          stroke="currentColor"
          stroke-width={0.5}
          stroke-dasharray="3,3"
          opacity={0.2}
        />
        {/* Y axis labels */}
        <text
          x={PAD_LEFT - 3}
          y={PAD_TOP + PLOT_H}
          text-anchor="end"
          dominant-baseline="middle"
          font-size="9"
          fill="currentColor"
          opacity={0.6}
        >
          0
        </text>
        <text
          x={PAD_LEFT - 3}
          y={PAD_TOP}
          text-anchor="end"
          dominant-baseline="middle"
          font-size="9"
          fill="currentColor"
          opacity={0.6}
        >
          {maxRemaining}
        </text>

        {/* X axis date labels */}
        <text
          x={xFor(0)}
          y={H - PAD_BOTTOM + 13}
          text-anchor="middle"
          font-size="9"
          fill="currentColor"
          opacity={0.6}
        >
          {firstDate}
        </text>
        {n > 1 && (
          <text
            x={xFor(n - 1)}
            y={H - PAD_BOTTOM + 13}
            text-anchor="middle"
            font-size="9"
            fill="currentColor"
            opacity={0.6}
          >
            {lastDate}
          </text>
        )}

        {/* Ideal burn-down line */}
        <line
          x1={idealX0}
          y1={idealY0}
          x2={idealX1}
          y2={idealY1}
          stroke="currentColor"
          stroke-width={1}
          stroke-dasharray="4,3"
          opacity={0.35}
        />

        {/* Actual remaining polyline */}
        {n === 1 ? (
          <circle
            cx={xFor(0)}
            cy={yFor(remainings[0])}
            r={3}
            fill="var(--color-primary)"
          />
        ) : (
          <polyline
            points={actualPoints}
            fill="none"
            stroke="var(--color-primary)"
            stroke-width={1.5}
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        )}

        {/* Dot on last actual point */}
        <circle
          cx={xFor(n - 1)}
          cy={yFor(remainings[n - 1])}
          r={2.5}
          fill="var(--color-primary)"
        />
      </svg>
    );
  }

  return (
    <div class="flex flex-col gap-2 px-2 pb-2 pt-1.5">
      {/* Current counts */}
      <div class="flex items-center gap-3 text-label text-[color:var(--color-on-surface-variant)]">
        <span>
          Offen{" "}
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {current.remaining}
          </span>{" "}
          / Gesamt{" "}
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {current.total}
          </span>
        </span>
        <span>
          Erledigt{" "}
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {current.completed}
          </span>
        </span>
        {velocity !== undefined && (
          <span>
            Velocity:{" "}
            <span
              class="font-medium text-[color:var(--color-on-surface)]"
              title={velocityLoadFailed ? "Konnte nicht geladen werden." : undefined}
            >
              {velocityLoadFailed
                ? "n. v."
                : velocity !== null
                  ? velocity.toFixed(1)
                  : "—"}
            </span>
          </span>
        )}
      </div>

      {/* SVG chart or empty state */}
      {sorted.length === 0 ? (
        <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
          Noch keine Snapshots
        </p>
      ) : (
        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-1 text-[color:var(--color-on-surface-variant)]">
          {chartEl}
        </div>
      )}

      {/* Snapshot trigger */}
      {canEdit && (
        <button
          type="button"
          onClick={() => void handleSnapshot()}
          disabled={snapshotting}
          class="self-start rounded border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-label text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] transition-colors"
        >
          {snapshotting ? "Erstelle…" : "Snapshot heute erstellen"}
        </button>
      )}
    </div>
  );
}
